import { Button, LabelledField, Spinner } from "@mixitone/components";
import {
  Account,
  type TournamentCompetition,
  TournamentEntry,
  type TournamentPaymentMethod,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { setupModelRelations } from "@mixitone/oom";
import { formatDateAndTime } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import { type CustomComponentFieldProps, type FormFieldConfig, field } from "./FormFieldsDefinition";
import { TournamentCompetitionSearch } from "./TournamentCompetitionSearch";
import routeService from "services/RouteService";

interface PlayerFormState {
  competitions: TournamentCompetition[];
  registrations: TournamentRegistration[];
  entries: TournamentEntry[];
  searchValue: TournamentCompetition | null;
  pendingRegistrations: Array<{
    competition: TournamentCompetition;
  }>;
  pendingRemovals: Set<string>;
  paymentMethod: TournamentPaymentMethod | null;
}

export class TournamentPlayerFormController extends FormController<
  TournamentPlayer,
  {
    tournament_session_id: string;
  },
  PlayerFormState
> {
  static override initialState = {
    ...FormController.initialState,
    competitions: [] as TournamentCompetition[],
    registrations: [] as TournamentRegistration[],
    entries: [] as TournamentEntry[],
    searchValue: null,
    pendingRegistrations: [],
    pendingRemovals: new Set<string>(),
    paymentMethod: null,
  };

  override fields(): Record<string, FormFieldConfig> {
    return {
      name: field("name", "text", {
        group: "basic",
        label: "Name",
        required: true,
        placeholder: "e.g. John Smith",
      }),
      email: field("email", "text", { group: "basic", label: "Email", inputType: "email" }),
      mobile: field("mobile", "text", { group: "basic", label: "Mobile", inputType: "tel" }),
      confirmed: field("confirmed_at", "checkbox", {
        group: "competitions",
        label: "Confirmed",
        description: "Confirm this player for the tournament.",
        helpText: this.hasActiveRegistrations
          ? "The player will receive a confirmation email."
          : "Player must be registered for at least one competition to be confirmed.",
        disabled: !this.hasActiveRegistrations,
        onChange: (value: boolean) => {
          if (value && !this.hasActiveRegistrations) {
            return; // Don't allow confirmation if no active registrations
          }
          this.state.record.confirmed_at = value ? new Date() : undefined;
        },
      }),
      paid: field("paid_at", "checkbox", {
        group: "payment",
        label: "Paid",
        description: "Mark this player's payment as received.",
        helpText: this.paidByEntry
          ? "Payment is covered by another player on this entry."
          : "Cash or bank transfer payment has been received.",
        disabled: this.paidByEntry,
        value: () => Boolean(this.state.record.paid_at),
        onChange: (value: boolean) => {
          if (this.paidByEntry) return;
          this.state.record.paid_at = value ? new Date() : undefined;
        },
      }),
      competitions: field("competitions", "custom", {
        group: "competitions",
        label: "Add competition",
        description: "Select competitions to register for.",
        component: RegisteredCompetitionsField,
      }),
      checkIn: field("check_in", "custom", {
        group: "check_in",
        label: "Check in",
        description: "Check in to the tournament.",
        component: CheckInField,
        condition: (record: TournamentPlayer) =>
          record.tournamentSession.isRegistration || record.tournamentSession.isActive,
      }),
    };
  }

  override async onLoad(): Promise<void> {
    const session = await TournamentSession.query()
      .preload("competitions")
      .preload("paymentMethods")
      .find(this.props.attributes.tournament_session_id);

    // Load competitions for this tournament session
    const competitions = session.competitions;
    let registrations: TournamentRegistration[] = [];
    let entries: TournamentEntry[] = [];

    if (!this.state.newRecord) {
      // Load existing registrations for this player
      registrations = await TournamentRegistration.query()
        .eq("tournament_player_id", this.state.record.id!)
        .preload("competition")
        .preload("entry")
        .all();

      entries = await TournamentEntry.query()
        .in(
          "id",
          registrations.map((reg) => reg.tournament_entry_id!).filter((id): id is string => !!id),
        )
        .preload("registrations")
        .all();
      const entriesPlayers = await TournamentPlayer.query()
        .in(
          "id",
          entries.flatMap((e) => e.registrations.map((r) => r.tournament_player_id!)),
        )
        .all();
      setupModelRelations({
        players: entriesPlayers,
        entries,
        registrations: entries.flatMap((e) => e.registrations),
      });
    }

    if (this.state.record.tournament_payment_method_id) {
      this.state.paymentMethod =
        session.paymentMethods.findById(this.state.record.tournament_payment_method_id) ?? null;
    }

    setupModelRelations({ session, competitions, registrations, player: this.state.record });
    this.state.competitions = competitions;
    this.state.registrations = registrations;
    this.state.entries = entries;

    for (const entry of this.state.entries) {
      if (!entry.registrations.loaded) {
        await entry.registrations.load();
      }
    }
  }

  get paidByEntry(): boolean {
    const record = this.state.record;
    if (!record?.tournament_session_id) return false;
    if (!record.tournamentSession?.loaded) return false;

    const session = record.tournamentSession;
    if (session.payment_by_player_or_entry !== "entry") return false;

    return this.state.entries.some((entry) => {
      if (!entry.registrations.loaded) return false;
      const players = entry.players;
      if (players.length <= 1) return false;

      // Another player on the shared entry already paid
      if (players.some((p) => p.isPaid && p.id !== record.id)) return true;

      // Non-paying doubles partner: no payment method selected in entry-billing mode
      return !record.tournament_payment_method_id && !record.isPaid;
    });
  }

  /** Cash/manual bank can be ticked by admin; Akahu is confirmed by the bank. */
  get canManuallyMarkPaid(): boolean {
    if (this.paidByEntry) return false;
    return this.state.paymentMethod?.payment_type !== "akahu-one-off";
  }

  override async buildRecord(): Promise<TournamentPlayer> {
    return new TournamentPlayer({
      ...this.props.attributes,
      account_id: Account.current!.id,
      is_deleted: false,
    });
  }

  override findModel(id: string) {
    return TournamentPlayer.query().find(id);
  }

  actionSelectCompetition(competition: TournamentCompetition) {
    // Check if already registered or pending registration
    if (
      this.state.registrations.some(
        (reg) => !this.state.pendingRemovals.has(reg.id!) && reg.tournament_competition_id === competition.id,
      ) ||
      this.state.pendingRegistrations.some((reg) => reg.competition.id === competition.id)
    ) {
      return;
    }

    // Add to pending registrations
    this.state.pendingRegistrations.push({ competition });
    this.state.searchValue = null;
  }

  actionRemoveRegistration(registration: TournamentRegistration) {
    // Mark for removal on save
    this.state.pendingRemovals.add(registration.id!);

    // If player is confirmed but has no active registrations after this removal, unconfirm them
    if (this.state.record.isConfirmed && !this.hasActiveRegistrations) {
      this.state.record.confirmed_at = undefined;
    }
  }

  /**
   * Check if the player has any active registrations (current + pending - removals)
   */
  get hasActiveRegistrations(): boolean {
    const currentRegistrations = this.state.registrations.filter(
      (reg) => !this.state.pendingRemovals.has(reg.id!),
    ).length;

    const pendingRegistrations = this.state.pendingRegistrations.length;

    return currentRegistrations + pendingRegistrations > 0;
  }

  async actionCheckIn() {
    this.state.record.checked_in_at = new Date();
    await this.state.record.save();
  }

  override async saveRecord(player: TournamentPlayer) {
    player.account_id = Account.current!.id;

    await player.save();

    // Handle pending registrations
    for (const { competition } of this.state.pendingRegistrations) {
      // Create entry for this competition
      const entry = new TournamentEntry({
        tournament_competition_id: competition.id!,
        account_id: Account.current!.id,
        club_id: player.club_id,
        is_deleted: false,
      });
      await entry.save();

      // Create registration linking player to entry
      const registration = new TournamentRegistration({
        tournament_competition_id: competition.id!,
        tournament_entry_id: entry.id!,
        tournament_player_id: player.id!,
        account_id: Account.current!.id,
        club_id: player.club_id,
      });
      await registration.save();

      // Add to display list
      registration.competition.set(competition);
      registration.entry.set(entry);
      this.state.registrations = [...this.state.registrations, registration];
    }
    this.state.pendingRegistrations = [];

    // Handle removals
    for (const registrationId of this.state.pendingRemovals) {
      const registration = await TournamentRegistration.query().find(registrationId);
      if (!registration) continue;

      const entryId = registration.tournament_entry_id;
      await registration.destroy();

      if (entryId) {
        await TournamentEntry.destroyIfNoRegistrations(entryId);
      }
    }

    // Update display list
    this.state.registrations = this.state.registrations.filter((r) => !this.state.pendingRemovals.has(r.id!));
    this.state.pendingRemovals.clear();
  }

  /**
   * Override canDelete to prevent deletion once player has participated in tournament activities
   */
  override get canDelete() {
    // Allow deletion for new records
    if (this.state.newRecord) return true;

    const player = this.state.record;

    // Prevent deletion if player has checked in
    if (player.isCheckedIn) {
      return false;
    }

    // Prevent deletion if player has been withdrawn (they were active participants)
    if (player.isWithdrawn) {
      return false;
    }

    // Prevent deletion if player has been confirmed (they were expected participants)
    if (player.isConfirmed) {
      return false;
    }

    // Otherwise use the default behavior (check if record can be destroyed)
    return super.canDelete;
  }
}

const RegistrationOption = ApplicationView(({ registration }: { registration: TournamentRegistration }) => {
  const controller = TournamentPlayerFormController.use();
  const { entries } = controller.state;

  const entry = entries.find((e) => e.id === registration.tournament_entry_id);
  const partner = entry?.players.find((p) => p.id !== controller.state.record.id);

  return (
    <div
      key={registration.id}
      className="flex items-center justify-between p-2 border border-gray-300 rounded"
    >
      <div className="flex items-center">
        <div className="flex flex-col">
          {entry && (
            <Button
              type="button"
              kind="link"
              onClick={() => {
                routeService.openModal("/tournament/:id/entries/:entryId/edit", {
                  params: {
                    id: registration.competition.tournament_session_id!,
                    entryId: entry.id!,
                  },
                });
              }}
            >
              {registration.competition.name} {entry.seed && <>({entry.seed})</>}
            </Button>
          )}
          {!entry && <span>{registration.competition.name}</span>}
          {partner && (
            <span className="text-sm text-gray-500">
              with{" "}
              <Button
                type="button"
                kind="link"
                onClick={() => {
                  routeService.openModal("/tournament/:id/players/:playerId/edit", {
                    params: {
                      id: registration.competition.tournament_session_id!,
                      playerId: partner.id!,
                    },
                  });
                }}
              >
                {partner.name}
              </Button>
            </span>
          )}
        </div>
      </div>
      <Button
        type="button"
        kind="secondary"
        onClick={() => controller.actionRemoveRegistration(registration)}
      >
        Remove
      </Button>
    </div>
  );
});

export const RegisteredCompetitionsField = ApplicationView(() => {
  const controller = TournamentPlayerFormController.use();
  const { saving, newRecord, competitions, registrations, searchValue, pendingRemovals, entries } =
    controller.state;

  // Check if any entry has unloaded registrations (stale data after entry was updated elsewhere)
  const hasUnloadedRegistrations = entries.some((entry) => entry && !entry.registrations.loaded);

  if (hasUnloadedRegistrations) {
    return (
      <fieldset disabled={saving} className="relative mb-4">
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75 rounded">
          <Spinner size={32} />
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset disabled={saving} className="relative mb-4">
      {!newRecord && (
        <>
          <LabelledField label="Add competition" htmlFor="competition_search">
            <TournamentCompetitionSearch
              competitions={competitions.filter(
                (comp) =>
                  !registrations.some(
                    (reg) => !pendingRemovals.has(reg.id!) && reg.tournament_competition_id === comp.id,
                  ),
              )}
              value={searchValue}
              onSelect={(competition: TournamentCompetition) =>
                controller.actionSelectCompetition(competition)
              }
            />
          </LabelledField>

          {(registrations.length > 0 || controller.state.pendingRegistrations.length > 0) && (
            <div className="mt-4">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: old */}
              <label className="block mb-2 text-sm font-medium">Registered competitions</label>
              <div className="space-y-2">
                {/* Show existing registrations */}
                {registrations
                  .filter((reg) => !pendingRemovals.has(reg.id!))
                  .map((registration) => (
                    <RegistrationOption registration={registration} key={registration.domKey("option")} />
                  ))}
                {/* Show pending registrations */}
                {controller.state.pendingRegistrations.map(({ competition }) => (
                  <div
                    key={`pending-${competition.id}`}
                    className="flex items-center justify-between p-2 border border-gray-300 rounded"
                  >
                    <div>{competition.name}</div>
                    <Button
                      type="button"
                      kind="secondary"
                      onClick={() => {
                        controller.state.pendingRegistrations = controller.state.pendingRegistrations.filter(
                          (reg) => reg.competition.id !== competition.id,
                        );

                        // If player is confirmed but has no active registrations after this removal, unconfirm them
                        if (controller.state.record.isConfirmed && !controller.hasActiveRegistrations) {
                          controller.state.record.confirmed_at = undefined;
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </fieldset>
  );
});

export const CheckInField = ApplicationView(({ record }: CustomComponentFieldProps<TournamentPlayer>) => {
  const controller = TournamentPlayerFormController.use();

  if (record.checked_in_at) {
    return <p>Player checked in at {formatDateAndTime(record.checked_in_at)}</p>;
  }

  return <Button onClick={() => controller.actionCheckIn()}>Check in</Button>;
});
